import { yupResolver } from '@hookform/resolvers/yup';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import WorkIcon from '@mui/icons-material/Work';
import { Grid } from '@mui/material';
import { AxiosError } from 'axios';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import {
  CareerBuilderEdgeSearch,
  fetchJobBoardCandidate,
  searchCareerBuilderEdge,
} from 'wisestep-api';
import { WsFilterTags, WsPagination } from 'wisestep-base';
import * as yup from 'yup';

import { PageHeader } from '../../../components/PageHeader';
import conversationWidgetStore from '../../../features/conversationWidget/store/conversationWidgetStore';
import { ITrackCvViewJobBoards } from '../../../features/conversationWidget/utils/cvHelper';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { useConversationWidgetStore } from '../../../utils/useConversationWidgetStore';
import TalentPoolListSideNav from '../../candidates/talentpool/components/talentpoolListSideNav';
import BotCustomization from '../Bots/customization/BotCustomizaton';
import CandidateCard from '../components/CandidateCard';
import NativeSearchForm from '../components/NativeSearchForm';
import BasicSearchForm from '../types/IBasicSearchForm';
import IQueryParam from '../types/IQueryParam';
import basicSearchDefaultValues from '../utils/nativeSearchHelper';

import AdvanceFilter from './components/AdvanceFilters';
import CareerBuilderEdgeCandidates from './types/CareerBuilderEdgeCandidates';
import CareerBuilderResponse from './types/CareerBuilderEdgeResponse';
import CareerBuilderAdvanceSearch, {
  AdvancedSearchKeys,
} from './types/ICareerBuilderEdgeAdvanceSearch';
import {
  defaultCareerBuilderEdgeSearchStateReq,
  ICareerBuilderEdgeSearchStateProps,
  pushSearchFilters,
} from './utils/CareerBuilderEdgeHelper';
import constructQuery from './utils/constructQuery';
import defaultAdvanceFilterValues from './utils/defaultAdvanceFilterValues';
import { processCareerBuilderEdgeFiltersResponse } from './utils/processCareerBuilderEdgeFiltersResponse';
import { processCareerBuilderEdgeResponse } from './utils/processCareerBuilderEdgeResponse';

const CareerBuilderEdge = () => {
  const selectedJob = conversationWidgetStore((state) => state.selectedJob);

  const setNotification = useNotificationStore((state) => state.setNotification);
  const setCvViewSourcePage = useConversationWidgetStore((state) => state.setCvViewSourcePage);
  const [state, setState] = useState<ICareerBuilderEdgeSearchStateProps>(
    defaultCareerBuilderEdgeSearchStateReq
  );
  const [careerBuilderResponse, setCareerBuilderResponse] = useState<CareerBuilderResponse>({
    candidates: [],
  });
  const methods = useForm<BasicSearchForm & CareerBuilderAdvanceSearch>({
    defaultValues: {
      ...basicSearchDefaultValues,
      ...defaultAdvanceFilterValues,
      lastActive: '[NOW-30DAYS/DAY TO *]',
      sort: 'relevancyMost',
    },
    resolver: yupResolver(
      yup.object().shape({ query: yup.string().required('Search query is required') })
    ),
  });
  const setAdvanceFilterField = (targetField: AdvancedSearchKeys, value) =>
    methods.setValue(targetField, value);
  const emptyAdvanceFieldValues = {
    ...defaultAdvanceFilterValues,
    ...{ excludeSocialOnlyProfiles: false, additionalFilters: [], contactMethod: [] },
  };
  const selectedIndex = (index: number, value: string) => console.log(index, value);
  const [openAdvanceFilter, setOpenAdvanceFilter] = useState(false);
  const [facets, setFacets] = useState<CareerBuilderAdvanceSearch>();
  const page = methods.watch('page');
  const accountId = methods.watch('accountId');

  const [careerBuilderEdgeRequest, setCareerBuilderEdgeRequest] =
    useState<CareerBuilderEdgeSearch>();
  const [showBotConfig, setShowBotConfig] = useState<boolean>(false);
  const [openSourceBot, setOpenSourceBot] = useState<boolean>(false);
  const [showConfigPanel, setShowConfigPanel] = useState<boolean>(true);

  const performSearch = async (values: BasicSearchForm & CareerBuilderAdvanceSearch) => {
    setOpenAdvanceFilter(false);
    setState((m) => ({
      ...m,
      appliedFilters: pushSearchFilters(values),
    }));

    try {
      const query: CareerBuilderEdgeSearch = constructQuery(values);
      setCareerBuilderEdgeRequest(query);
      setOpenSourceBot(false);
      setShowConfigPanel(true);
      setShowBotConfig(true);
      const data = await searchCareerBuilderEdge(query);
      const response = processCareerBuilderEdgeResponse(data.data.data);
      const filters = processCareerBuilderEdgeFiltersResponse(response.facets);
      setFacets(filters);
      setCareerBuilderResponse(response);
    } catch (e) {
      console.log('error', e);
    }
  };
  useEffect(() => {
    if (methods.formState.errors.query?.message) {
      setOpenAdvanceFilter(false);
      setNotification(failed('Search Query is Required'));
    }
  }, [methods.formState.errors.query, setNotification]);
  const isDownloaded = (candidate: CareerBuilderEdgeCandidates): boolean =>
    careerBuilderResponse.existing?.some(
      (existingCandidate) => existingCandidate.candidateJobBoardId === candidate.EdgeID
    ) ?? false;

  const handleSourceBotBtnClick = () => {
    if (!careerBuilderEdgeRequest) {
      setNotification(
        failed('Please run a candidate search for which you want to create Sourcing bots.')
      );
      return;
    }
    setOpenSourceBot(true);
    setShowConfigPanel(false);
    setShowBotConfig(true);
  };

  const onDeleteFilterTag = (tag: string) => {
    const idOfChip = state.appliedFilters.find((existChip) => existChip.tag === tag)?.id ?? '';
    if (!isEmpty(idOfChip)) {
      setAdvanceFilterField(idOfChip as AdvancedSearchKeys, emptyAdvanceFieldValues[idOfChip]);
      methods.handleSubmit(performSearch)();
      const index = state.appliedFilters.findIndex((existChip) => existChip.id === idOfChip);
      state.appliedFilters.splice(index, 1);
      setState((m) => ({
        ...m,
        appliedFilters: [...state.appliedFilters],
      }));
    }
  };

  return (
    <>
      <PageHeader title="CareerBuilderEdge" />
      <Grid container spacing={2}>
        <Grid item xs={3}>
          <TalentPoolListSideNav selectedIndexValue={selectedIndex} />
        </Grid>
        <Grid item xs={9}>
          <FormProvider {...methods}>
            <NativeSearchForm
              jobBoardName="CAREER_BUILDER_EDGE"
              performSearch={performSearch}
              openAdvanceFilter={setOpenAdvanceFilter}
              handleSourceBotBtnClick={handleSourceBotBtnClick}
              onViewCvClick={async (candidateId) => {
                try {
                  const candidateData = await fetchJobBoardCandidate({
                    jobBoardAccountId: accountId,
                    jobBoardId: candidateId,
                    force: false,
                    harvestingId: '',
                  });
                  setCvViewSourcePage(ITrackCvViewJobBoards.CareerBuilderEDGE);
                  const queryId = `query${uuidv4()}`;
                  localStorage.setItem(queryId, encodeURI(methods.getValues('query') ?? ''));
                  openUrlInNewTab(
                    `/candidate-layout/profile?id=${
                      candidateData?.newCandidateData?.id
                    }&array=${[]}&query=${queryId}&accountId=${accountId}&jobBoardId=${candidateId}`
                  );
                } catch (error) {
                  const err = error as AxiosError;
                  if (err.response?.data.status) {
                    setNotification(failed(err.response?.data.status));
                  } else {
                    setNotification(failed('There was some problem in fetching the candidate.'));
                  }
                }
              }}
            />
            <AdvanceFilter
              performSearch={performSearch}
              open={openAdvanceFilter}
              onClose={() => setOpenAdvanceFilter(false)}
              facets={facets}
            />
            {showBotConfig && (
              <BotCustomization
                name={careerBuilderEdgeRequest?.keywords}
                request={careerBuilderEdgeRequest}
                jobBoardName="CAREER_BUILDER_EDGE"
                accountId={accountId}
                openSourceBot={openSourceBot}
                showConfigPanel={showConfigPanel}
                setOpenSourceBotBool={(val: boolean) => setOpenSourceBot(val)}
                setShowConfigPanelBool={(val: boolean) => setShowConfigPanel(val)}
                queryError={methods.watch('query')}
              />
            )}
            <WsFilterTags filtersList={state.appliedFilters} onDelete={onDeleteFilterTag} />
            {careerBuilderResponse.totalItems && careerBuilderResponse.totalItems > 0 && (
              <WsPagination
                count={Math.ceil(careerBuilderResponse.totalItems / 50)}
                onChange={(_, value) => {
                  methods.setValue('page', value + 1);
                  methods.handleSubmit(performSearch)();
                }}
                itemsPerPage={50}
                pageNumber={page - 1}
                defaultPage={page}
                isPageCount={true}
                type="Candidates"
                totalCount={careerBuilderResponse.totalItems}
              />
            )}
            {careerBuilderResponse?.candidates.map((candidate) => (
              <Grid my={2} key={candidate.EdgeID} item xs={12}>
                <CandidateCard
                  candidateId={candidate.EdgeID}
                  title={candidate.candidateData.name ?? ''}
                  name={candidate.candidateData.name ?? ''}
                  imgSrc={''}
                  isDownloaded={isDownloaded(candidate)}
                  skills={Object.keys(candidate.Keywords)}
                  dateLastActive={candidate.Attributes.LastAccessed ?? ''}
                  dateResumeLastUpdated={candidate.ResumeLastModified}
                  onViewCvClick={async (candidateId) => {
                    try {
                      const data = await fetchJobBoardCandidate({
                        jobBoardAccountId: accountId,
                        jobBoardId: candidateId,
                        force: false,
                        harvestingId: '',
                        candidateData: {
                          ...candidate.candidateData,
                          jobId: selectedJob?.id ?? '',
                          team: selectedJob?.activeWorkAssignment?.team ?? null,
                        },
                      });
                      const candidateData = data.newCandidateData;
                      const params: IQueryParam = {
                        resumeUpdatedOn: candidate.ResumeLastModified,
                        accountId: accountId,
                        jobBoardId: candidateId,
                        query: encodeURI(methods.getValues('query') ?? ''),
                        jobBoard: 'CareerBuilder',
                      };
                      const paramId = `param${uuidv4()}`;
                      localStorage.setItem(paramId, getEncodedQueryParams(params));
                      openUrlInNewTab(
                        `/candidate-layout/profile?id=${
                          candidateData?.id
                        }&array=${[]}&q=${getEncodedQueryParams(params)}`
                      );
                    } catch (error) {
                      const err = error as AxiosError;
                      if (err.response?.data.status) {
                        setNotification(failed(err.response?.data.status));
                      }
                    }
                  }}
                  detailItems={[
                    {
                      icon: <LocationOnIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
                      label: `${candidate.Location.City},${candidate.Location.State} ${candidate.Location.Country}`,
                      tooltip: 'Location',
                    },
                    {
                      icon: <LibraryBooksIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
                      label: `Job Title: ${candidate.JobTitle}`,
                      tooltip: 'Job Title',
                    },
                    {
                      icon: <PeopleIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
                      label: `Experience: ${candidate.YearsOfExperience}`,
                      tooltip: 'Experience',
                    },
                    {
                      icon: <WorkIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
                      label: `Recent Employer: ${candidate.Employments[0]?.Employer ?? ''}`,
                      tooltip: 'Recent Employer',
                    },
                    {
                      icon: <PeopleIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
                      label: `Source: ${candidate.candidateData.source}`,
                      tooltip: 'Source',
                    },
                    {
                      icon: <LibraryBooksIcon sx={{ color: '#888A8E', fontSize: '1.27rem' }} />,
                      label: `Edge Score: ${candidate.EdgeScore ?? 'N/A'}`,
                      tooltip: 'Edge Score',
                    },
                  ]}
                />
              </Grid>
            ))}
          </FormProvider>
        </Grid>
      </Grid>
    </>
  );
};

export default CareerBuilderEdge;
